'use client';
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useQuery } from '@tanstack/react-query';
import { Download, ExternalLink, FileText } from 'lucide-react';
import dynamic from 'next/dynamic';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { LoadingSpinner } from '../ui/loading-spinner';
var PdfViewer = dynamic(function () { return import('./pdf-viewer').then(function (mod) { return ({ default: mod.PdfViewer }); }); }, {
    ssr: false,
    loading: function () { return (<div className="flex h-full items-center justify-center">
        <LoadingSpinner size={40}/>
      </div>); },
});
var Fallback = function (_a) {
    var document = _a.document;
    return (<div className="bg-muted flex h-full items-center justify-center">
      <Card className="w-96 py-8">
        <CardContent className="px-8 text-center">
          <FileText className="text-muted-foreground mx-auto mb-4 h-16 w-16"/>
          <h3 className="text-foreground mb-2 text-lg font-semibold">{document.name}</h3>
          <p className="text-muted-foreground mb-6 text-sm">
            Preview not available for {document.extension.toUpperCase()} files
          </p>
          <div className="space-y-2">
            <Button onClick={function () { return window.open(document.signedUrl, '_blank'); }} className="w-full">
              <ExternalLink className="mr-2 h-4 w-4"/>
              Open in New Tab
            </Button>
            <Button variant="outline" onClick={function () {
            if (document.signedUrl) {
                var link = window.document.createElement('a');
                link.href = document.signedUrl;
                link.download = document.name;
                link.click();
            }
        }} className="w-full">
              <Download className="mr-2 h-4 w-4"/>
              Download File
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>);
};
export function useGetTextContent(document) {
    var _this = this;
    var _a;
    var isTxt = ((_a = document === null || document === void 0 ? void 0 : document.extension) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === 'txt';
    // Fetch text content for .txt files
    var _b = useQuery({
        queryKey: ['document-text', document === null || document === void 0 ? void 0 : document.signedUrl],
        queryFn: function () { return __awaiter(_this, void 0, void 0, function () {
            var response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!(document === null || document === void 0 ? void 0 : document.signedUrl)) {
                            return [2 /*return*/];
                        }
                        return [4 /*yield*/, fetch(document.signedUrl)];
                    case 1:
                        response = _a.sent();
                        if (!response.ok) {
                            throw new Error("Failed to fetch text content: ".concat(response.status, " ").concat(response.statusText));
                        }
                        return [2 /*return*/, response.text()];
                }
            });
        }); },
        enabled: isTxt && !!(document === null || document === void 0 ? void 0 : document.signedUrl),
        retry: 1,
        staleTime: 1000 * 60 * 60 * 24, // 24 hours
        gcTime: 1000 * 60 * 60 * 24, // 24 hours
    }), textContent = _b.data, textError = _b.error, isLoadingText = _b.isLoading;
    return {
        isLoadingText: isLoadingText,
        textContent: textContent,
        textError: textError,
    };
}
export function DocumentViewer(_a) {
    var _b, _c;
    var document = _a.document, embedUrl = _a.embedUrl, textContent = _a.textContent, _d = _a.isLoadingText, isLoadingText = _d === void 0 ? false : _d, _e = _a.textError, textError = _e === void 0 ? null : _e, searchText = _a.searchText, _f = _a.allowFullscreen, allowFullscreen = _f === void 0 ? false : _f;
    if (!document)
        return null;
    if (embedUrl) {
        return (<div className="bg-muted flex h-full items-center justify-center">
        <div className="bg-card h-full w-full border shadow-lg">
          <iframe src={embedUrl} width="100%" height="100%" title="Document Viewer" className="h-full w-full"/>
        </div>
      </div>);
    }
    var extension = (_c = (_b = document === null || document === void 0 ? void 0 : document.extension) === null || _b === void 0 ? void 0 : _b.toLowerCase()) !== null && _c !== void 0 ? _c : '';
    var hasPreview = document
        ? ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt'].includes(extension)
        : false;
    var isPdf = extension === 'pdf';
    var isTxt = extension === 'txt';
    if (!hasPreview) {
        return <Fallback document={document}/>;
    }
    return (<div className="bg-muted flex h-full items-center justify-center">
      <div className="bg-card h-full w-full border shadow-lg transition-transform" style={{
            transformOrigin: 'center center',
        }}>
        {isTxt && (<div className="h-full w-full overflow-auto p-8">
            {isLoadingText ? (<div className="flex h-full items-center justify-center">
                <LoadingSpinner size={40}/>
              </div>) : textError ? (<div className="flex h-full flex-col items-center justify-center">
                <p className="text-destructive mb-4 text-sm">
                  Failed to load text content:{' '}
                  {textError instanceof Error ? textError.message : 'Unknown error'}
                </p>
                <Fallback document={document}/>
              </div>) : textContent ? (<pre className="text-foreground font-mono text-sm leading-relaxed whitespace-pre-wrap">
                {textContent}
              </pre>) : (<Fallback document={document}/>)}
          </div>)}
        {!isPdf && !isTxt && (<iframe src={"https://view.officeapps.live.com/op/embed.aspx?src=".concat(encodeURIComponent(document.signedUrl), "&embedded=true")} width="100%" height="100%" title="Document Viewer" className="h-full w-full"/>)}
        {isPdf && (<PdfViewer fallback={<Fallback document={document}/>} src={document.signedUrl} searchText={searchText} allowFullscreen={allowFullscreen}/>)}
      </div>
    </div>);
}
